import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Popover, TextField, Typography, useTheme } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import { Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { MetaData } from "../../../Login/typings/login.types";
import { ColorPicker } from "../../../ColorPicker/ColorPicker.component";
import { FileObject } from "../../../FileDropzone/typings/FileObject.type";
import { convertToFileObject } from "../../../FileDropzone/functions/convertToFileObject.function";

type Props = {
  newValues: MetaData;
  companyName: string | null;
  brandColor?: string | null;
  logoUrl: string | null;
  logoFile: FileObject[] | undefined;
  setNewValues: Dispatch<SetStateAction<MetaData>>;
  onEditLogo: () => void;
  /** When provided (create flow), company name is editable. */
  setCompanyName?: (name: string) => void;
  /** When provided (create flow), logo click opens file picker and sets the chosen file. */
  setLogoFile?: Dispatch<SetStateAction<FileObject[] | undefined>>;
  /** When provided (edit flow), shows delete company control in the header. */
  onDeleteCompany?: () => void;
};

export const CompanyCardSettingDialogHeader = ({
  newValues,
  companyName,
  brandColor,
  logoUrl,
  logoFile,
  onEditLogo,
  setNewValues,
  setCompanyName,
  setLogoFile,
  onDeleteCompany
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [hasError, setHasError] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const openEl = Boolean(anchorEl);

  const isCreateMode = setCompanyName != null;
  const handleLogoClick = () => {
    if (setLogoFile) {
      fileInputRef.current?.click();
    } else {
      onEditLogo();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !setLogoFile) return;
    try {
      const fileObject = await convertToFileObject(file as File & { path?: string });
      setLogoFile([fileObject]);
    } finally {
      e.target.value = "";
    }
  };

  /**
   * 1️⃣ image source priority:
   * - uploaded file preview
   * - existing logoUrl
   */
  const imageSrc = useMemo(() => {
    if (logoFile?.length && logoFile[0]?.file) {
      return URL.createObjectURL(logoFile[0].file);
    }
    return logoUrl;
  }, [logoFile, logoUrl]);

  const showImage = Boolean(imageSrc) && !hasError;
  const companyInitial = companyName?.charAt(0).toUpperCase();

  const basicCompColor = brandColor ?? theme.palette.primary.light;
  const companyColor = newValues.color ?? basicCompColor;

  useEffect(() => {
    setHasError(false);
  }, [imageSrc]);

  useEffect(() => {
    return () => {
      if (logoFile?.[0]?.file && imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [logoFile, imageSrc]);

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: "none" }}
      />
      <Box
        sx={{
          position: "relative",
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider"
        }}
      >
        {onDeleteCompany ? (
          <IconButton
            data-testid="delete-company-btn"
            onClick={onDeleteCompany}
            aria-label={t("Delete")}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <Delete />
          </IconButton>
        ) : null}

        <Box display="flex" alignItems="center" gap={2} sx={{ pr: onDeleteCompany ? 5 : 0 }}>
          {/* Logo */}
          <Box
            onClick={handleLogoClick}
            sx={{
              width: 130,
              height: 130,
              borderRadius: showImage ? 2 : 16,
              overflow: "hidden",
              bgcolor: showImage ? "transparent" : companyColor,
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              cursor: "pointer",
              position: "relative",
              flexShrink: 0,
              "&:hover .logo-overlay": { opacity: 1 }
            }}
          >
            {showImage ? (
              <Box
                component="img"
                src={imageSrc!}
                alt={`${companyName} logo`}
                onError={() => setHasError(true)}
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <Typography variant="h3">{companyInitial}</Typography>
            )}

            {/* Hover overlay */}
            <Box
              className="logo-overlay"
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(0,0,0,0.45)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity 0.2s"
              }}
            >
              <ImageIcon />
            </Box>
          </Box>

          {/* <Box sx={{ width: "calc(95% - 130px)" }}> */}
          <Box sx={{ width: "100%" }}>
            {/* Right side: name + color */}
            <Box>
              {isCreateMode && setCompanyName ? (
                <TextField
                  fullWidth
                  margin="dense"
                  label={t("Company name")}
                  slotProps={{ htmlInput: { "data-testid": "name-input" } }}
                  value={companyName ?? ""}
                  onChange={e => setCompanyName(e.target.value)}
                  variant="standard"
                  sx={{ mb: 2 }}
                />
              ) : (
                <Typography
                  component="h3"
                  sx={{
                    fontSize: 17,
                    fontWeight: 500,
                    color: "#111827",
                    lineHeight: 1.3,
                    minWidth: 0,
                    mb: 2
                  }}
                >
                  {companyName}
                </Typography>
              )}

              {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}> */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, maxWidth: 360 }}>
                <Box sx={{ background: companyColor, flexGrow: 1, height: 24, borderRadius: 2 }} />
                <IconButton
                  size="small"
                  onClick={e => setAnchorEl(e.currentTarget)}
                  data-testid="change-company-color-btn"
                  aria-label="change Color"
                >
                  <EditIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Color picker component */}
      <Popover
        open={openEl}
        anchorEl={anchorEl}
        /* istanbul ignore next */
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { style: { background: theme.palette.background.paper } } }}
      >
        <ColorPicker
          color={companyColor}
          setColor={newColor => setNewValues(prevState => ({ ...prevState, color: newColor }))}
        />
      </Popover>
    </>
  );
};
